import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const DEFAULT_FETCH_FIELDS = ['formattedAddress', 'location', 'addressComponents'];

const isPlacesNewBlockedError = (error) => {
  const normalized = `${error?.message || ''} ${error?.toString?.() || ''}`.toLowerCase();
  return (
    normalized.includes('403') ||
    normalized.includes('forbidden') ||
    normalized.includes('blocked') ||
    normalized.includes('autocompleteplaces') ||
    normalized.includes('places.googleapis.com')
  );
};

const extractSuggestionText = (prediction) => {
  if (!prediction) return '';
  if (prediction.text && typeof prediction.text.toString === 'function') {
    return prediction.text.toString();
  }
  if (prediction.mainText && typeof prediction.mainText.toString === 'function') {
    return prediction.mainText.toString();
  }
  return prediction.description || '';
};

const normalizeAddressComponents = (components = []) =>
  components.map((component) => ({
    long_name: component.longText || component.long_name || '',
    short_name: component.shortText || component.short_name || '',
    types: component.types || []
  }));

const toLegacyPlace = (place) => {
  const rawLocation = place?.location || place?.geometry?.location;
  const latValue =
    typeof rawLocation?.lat === 'function' ? rawLocation.lat() : rawLocation?.lat;
  const lngValue =
    typeof rawLocation?.lng === 'function' ? rawLocation.lng() : rawLocation?.lng;

  return {
    address_components: normalizeAddressComponents(place?.addressComponents || place?.address_components || []),
    formatted_address: place?.formattedAddress || place?.formatted_address || '',
    geometry: {
      location: {
        lat: () => latValue,
        lng: () => lngValue
      }
    }
  };
};

const GooglePlacesAutocomplete = ({
  children,
  fetchFields = DEFAULT_FETCH_FIELDS,
  includedPrimaryTypes,
  includedRegionCodes,
  locationBias,
  locationRestriction,
  minLength = 3,
  onLoad,
  onPlaceChanged
}) => {
  const child = React.Children.only(children);
  const inputElementRef = useRef(null);
  const placesLibraryRef = useRef(null);
  const requestCounterRef = useRef(0);
  const selectedPlaceRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const isSelectingRef = useRef(false);
  const modeRef = useRef('new');
  const legacyAutocompleteRef = useRef(null);
  const legacyListenerRef = useRef(null);
  const wrapperRef = useRef(null);

  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const enableLegacyAutocomplete = useCallback(() => {
    const inputElement = inputElementRef.current || wrapperRef.current?.querySelector('input');
    if (!inputElement || !window.google?.maps?.places?.Autocomplete) {
      return false;
    }

    modeRef.current = 'legacy';
    setSuggestions([]);
    setIsOpen(false);
    setHighlightedIndex(-1);

    legacyListenerRef.current?.remove?.();
    legacyAutocompleteRef.current = new window.google.maps.places.Autocomplete(inputElement);
    legacyListenerRef.current = legacyAutocompleteRef.current.addListener(
      'place_changed',
      () => {
        selectedPlaceRef.current = legacyAutocompleteRef.current.getPlace();
        onPlaceChanged?.();
      }
    );

    console.warn('Places API (new) bloqueada. Se activa fallback a Autocomplete legacy.');
    return true;
  }, [onPlaceChanged]);

  useEffect(() => {
    const proxy = {
      getPlace: () => selectedPlaceRef.current
    };
    onLoad?.(proxy);
  }, [onLoad]);

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      const inputElement = wrapperRef.current?.querySelector('input');
      if (!window.google?.maps || !inputElement) return;
      inputElementRef.current = inputElement;

      try {
        if (window.google.maps.importLibrary) {
          const places = await window.google.maps.importLibrary('places');
          if (cancelled) return;

          placesLibraryRef.current = places;
          const hasNewAutocomplete =
            Boolean(places?.AutocompleteSuggestion) &&
            typeof places.AutocompleteSuggestion.fetchAutocompleteSuggestions === 'function';

          if (hasNewAutocomplete) {
            modeRef.current = 'new';
            return;
          }
        }
      } catch (error) {
        console.error('No se pudo inicializar Places API (new), se usara fallback legacy.', error);
      }

      if (window.google.maps.places?.Autocomplete) {
        enableLegacyAutocomplete();
      }
    };

    initialize();

    return () => {
      cancelled = true;
      legacyListenerRef.current?.remove?.();
    };
  }, [enableLegacyAutocomplete]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const fetchSuggestions = useCallback(
    async (value) => {
      if (modeRef.current !== 'new') return;
      const trimmed = value.trim();
      if (!trimmed || trimmed.length < minLength) {
        setIsOpen(false);
        setSuggestions([]);
        setHighlightedIndex(-1);
        return;
      }

      try {
        const places =
          placesLibraryRef.current || (await window.google.maps.importLibrary('places'));
        placesLibraryRef.current = places;

        if (
          !places?.AutocompleteSuggestion ||
          typeof places.AutocompleteSuggestion.fetchAutocompleteSuggestions !== 'function'
        ) {
          modeRef.current = 'legacy';
          return;
        }

        if (!sessionTokenRef.current && places.AutocompleteSessionToken) {
          sessionTokenRef.current = new places.AutocompleteSessionToken();
        }

        const requestId = requestCounterRef.current + 1;
        requestCounterRef.current = requestId;

        const request = {
          input: trimmed,
          sessionToken: sessionTokenRef.current
        };

        if (includedPrimaryTypes?.length) {
          request.includedPrimaryTypes = includedPrimaryTypes;
        }
        if (includedRegionCodes?.length) {
          request.includedRegionCodes = includedRegionCodes;
        }
        if (locationBias) {
          request.locationBias = locationBias;
        }
        if (locationRestriction) {
          request.locationRestriction = locationRestriction;
        }

        const response = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          request
        );
        if (requestCounterRef.current !== requestId) return;

        const nextSuggestions = (response?.suggestions || [])
          .map((entry, index) => {
            const prediction = entry?.placePrediction;
            if (!prediction) return null;
            const text = extractSuggestionText(prediction);
            return {
              id: prediction.placeId || `${text}-${index}`,
              prediction,
              text: text || 'Ubicacion'
            };
          })
          .filter(Boolean);

        setSuggestions(nextSuggestions);
        setIsOpen(nextSuggestions.length > 0);
        setHighlightedIndex(nextSuggestions.length > 0 ? 0 : -1);
      } catch (error) {
        console.error('Error obteniendo sugerencias de Places API (new):', error);
        if (isPlacesNewBlockedError(error) && enableLegacyAutocomplete()) {
          return;
        }
        setSuggestions([]);
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    },
    [
      includedPrimaryTypes,
      includedRegionCodes,
      locationBias,
      locationRestriction,
      minLength,
      enableLegacyAutocomplete
    ]
  );

  const selectSuggestion = useCallback(
    async (suggestion) => {
      if (!suggestion?.prediction) return;

      try {
        const place = suggestion.prediction.toPlace();
        await place.fetchFields({ fields: fetchFields });
        const normalizedPlace = toLegacyPlace(place);
        selectedPlaceRef.current = normalizedPlace;

        setSuggestions([]);
        setIsOpen(false);
        setHighlightedIndex(-1);

        if (placesLibraryRef.current?.AutocompleteSessionToken) {
          sessionTokenRef.current = new placesLibraryRef.current.AutocompleteSessionToken();
        } else {
          sessionTokenRef.current = null;
        }

        const nextValue = normalizedPlace.formatted_address;
        const input = inputElementRef.current;
        if (input && nextValue) {
          isSelectingRef.current = true;
          const valueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            'value'
          )?.set;

          if (valueSetter) {
            valueSetter.call(input, nextValue);
          } else {
            input.value = nextValue;
          }

          input.dispatchEvent(new Event('input', { bubbles: true }));
        }

        onPlaceChanged?.();
      } catch (error) {
        console.error('Error seleccionando sugerencia de Places API (new):', error);
        if (isPlacesNewBlockedError(error)) {
          enableLegacyAutocomplete();
        }
      }
    },
    [enableLegacyAutocomplete, fetchFields, onPlaceChanged]
  );

  const handleChange = (event) => {
    child.props.onChange?.(event);

    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      return;
    }

    fetchSuggestions(event.target.value);
  };

  const handleKeyDown = (event) => {
    child.props.onKeyDown?.(event);

    if (!isOpen || suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        void selectSuggestion(suggestions[highlightedIndex]);
      }
      return;
    }

    if (event.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      {React.cloneElement(child, {
        autoComplete: 'off',
        onChange: handleChange,
        onKeyDown: handleKeyDown
      })}

      {isOpen && suggestions.length > 0 && (
        <SuggestionsList role="listbox">
          {suggestions.map((suggestion, index) => (
            <SuggestionItem
              $active={index === highlightedIndex}
              key={suggestion.id}
              onMouseDown={(event) => {
                event.preventDefault();
                void selectSuggestion(suggestion);
              }}
              role="option"
            >
              {suggestion.text}
            </SuggestionItem>
          ))}
        </SuggestionsList>
      )}
    </Wrapper>
  );
};

GooglePlacesAutocomplete.propTypes = {
  children: PropTypes.node.isRequired,
  fetchFields: PropTypes.arrayOf(PropTypes.string),
  includedPrimaryTypes: PropTypes.arrayOf(PropTypes.string),
  includedRegionCodes: PropTypes.arrayOf(PropTypes.string),
  locationBias: PropTypes.shape({}),
  locationRestriction: PropTypes.shape({}),
  minLength: PropTypes.number,
  onLoad: PropTypes.func,
  onPlaceChanged: PropTypes.func
};

export default GooglePlacesAutocomplete;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SuggestionsList = styled.ul`
  background: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  box-shadow: 0 6px 16px rgb(0 0 0 / 10%);
  left: 0;
  list-style: none;
  margin: ${({ theme }) => theme.sizing.xxs} 0 0;
  max-height: 220px;
  overflow-y: auto;
  padding: ${({ theme }) => theme.sizing.xxs} 0;
  position: absolute;
  top: 100%;
  width: 100%;
  z-index: 30;
`;

const SuggestionItem = styled.li`
  background: ${({ $active, theme }) =>
    $active ? theme.fill.defaultWeak : theme.fill.defaultMain};
  color: ${({ theme }) => theme.colors.defaultMain};
  cursor: pointer;
  font-size: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => `${theme.sizing.xxs} ${theme.sizing.xs}`};
`;
